import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { pct, EMDASH } from "@/components/player/statFormat";
import { ShieldCheck, Info, Trophy, ChevronRight } from "lucide-react";

const CATEGORIES = [["hitting", "Hitting"], ["pitching", "Pitching"], ["fielding", "Fielding"], ["twoway", "Two-Way"]];
const CONF = { high: "bg-emerald-100 text-emerald-800", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-500" };
const CONTEXTS = [["all", "All Approved Games"], ["cbr_nation", "CBR Nation Only"], ["regular_team", "Regular Teams Only"]];

// friendly labels; anything missing falls back to a formatted key
const LABELS = {
  avg: "AVG", obp: "OBP", slg: "SLG", ops: "OPS", h: "Hits", r: "Runs", rbi: "RBI", hr: "HR",
  doubles: "2B", triples: "3B", bb: "BB", so: "SO", sb: "SB", iso: "ISO", bb_pct: "BB%", k_pct: "K%",
  qab: "QAB", qab_pct: "QAB%",
  hitting_score: "Hitting Score",
  era: "ERA", whip: "WHIP", ip_outs: "IP", k9: "K/9", bb9: "BB/9", h9: "H/9", hr9: "HR/9", kbb: "K/BB",
  opp_avg: "Opp AVG", fip: "FIP", w: "Wins", sv: "Saves", pitching_score: "Pitching Score",
  fielding_pct: "Fielding %", po: "Putouts", a: "Assists", dp: "Double Plays", of_assists: "Outfield Assists",
  e: "Errors", cs_pct: "Caught Stealing %", fielding_score: "Fielding Score",
  two_way_score: "Overall Two-Way Score",
};
const CHIPS = { hitting: ["avg", "ops", "hr", "rbi", "qab_pct"], pitching: ["era", "so", "whip", "ip_outs"], fielding: ["fielding_pct", "po", "a"], twoway: ["two_way_score"] };
const RATE3 = new Set(["avg", "obp", "slg", "ops", "iso", "opp_avg", "fielding_pct"]);
const RATE2 = new Set(["era", "whip", "fip"]);
const RATE1 = new Set(["k9", "bb9", "h9", "hr9", "kbb"]);
const PCTS = new Set(["bb_pct", "k_pct", "cs_pct", "qab_pct"]);

const label = (s) => LABELS[s] || s.replace(/_/g, " ").toUpperCase();
const fmtVal = (s, v) => {
  if (v === null || v === undefined) return EMDASH;
  if (s === "ip_outs") return (Math.floor(v / 3) + (v % 3) / 10).toFixed(1);
  if (s.endsWith("_score")) return Number(v).toFixed(1);
  if (RATE3.has(s)) return Number(v).toFixed(3).replace(/^0/, "");
  if (RATE2.has(s)) return Number(v).toFixed(2);
  if (RATE1.has(s)) return Number(v).toFixed(1);
  if (PCTS.has(s)) return pct(v);
  return Number.isInteger(v) ? String(v) : Number(v).toFixed(1);
};

const PROVINCES = ["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"];
const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const DEFAULT_STAT = { hitting: "h", pitching: "so", fielding: "po", twoway: "two_way_score" };
const CAT_PARAM = { hitting: "hitting", pitching: "pitching", fielding: "fielding", twoway: "twoway" };

export default function InGameLeaderboard() {
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Deep-link: ?hitting=avg / ?pitching=era / ?fielding=po / ?twoway=two_way_score
  const deep = CATEGORIES.map(([k]) => [k, params.get(CAT_PARAM[k])]).find(([, v]) => v);
  let category = deep ? deep[0] : (CATEGORIES.some(([k]) => k === params.get("cat")) ? params.get("cat") : null);
  let stat = deep ? deep[1] : params.get("stat");
  // smart default when no explicit selection: last used (localStorage) else a counting stat
  if (!category || !stat) {
    try {
      const saved = JSON.parse(localStorage.getItem("cbr_lb_last") || "null");
      if (!category && saved?.category) { category = saved.category; if (!stat) stat = saved.stat; }
    } catch { /* noop */ }
    category = category || "hitting";
    stat = stat || DEFAULT_STAT[category];
  }
  const context = params.get("ctx") || "all";
  const f = { graduation_year: params.get("grad") || "", province: params.get("prov") || "", position: params.get("pos") || "", tournament_id: params.get("tourn") || "" };

  const set = (obj) => { const p = new URLSearchParams(params); Object.entries(obj).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); }); setParams(p, { replace: true }); };

  const selectStat = (cat, st) => {
    const p = new URLSearchParams(params);
    ["hitting", "pitching", "fielding", "twoway", "cat", "stat"].forEach((k) => p.delete(k));
    p.set(CAT_PARAM[cat], st);
    setParams(p, { replace: true });
    try { localStorage.setItem("cbr_lb_last", JSON.stringify({ category: cat, stat: st })); } catch { /* noop */ }
  };

  useEffect(() => { api.get("/ingame/leaderboard-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const statList = cfg?.categories?.[category] || [];
  const kind = cfg?.stats_meta?.[category]?.[stat]?.kind;

  useEffect(() => {
    setLoading(true);
    const q = { category, stat, context, page_size: 100 };
    if (f.graduation_year) q.graduation_year = f.graduation_year;
    if (f.province) q.province = f.province;
    if (f.position) q.position = f.position;
    if (f.tournament_id) q.tournament_id = f.tournament_id;
    api.get("/ingame/leaderboard", { params: q }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    try { localStorage.setItem("cbr_lb_last", JSON.stringify({ category, stat })); } catch { /* noop */ }
    // eslint-disable-next-line
  }, [category, stat, context, f.graduation_year, f.province, f.position, f.tournament_id]);

  const statusBadge = useMemo(() => {
    if (!data) return null;
    const map = { official: ["Official", "bg-emerald-600 text-white"], provisional: ["Provisional", "bg-amber-500 text-white"], none: ["Not Qualified", "bg-slate-400 text-white"] };
    return map[data.ranks_status] || map.none;
  }, [data]);

  return (
    <div data-testid="ingame-leaderboard">
      {/* STEP 1 — category */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
        {CATEGORIES.map(([k, lbl]) => (
          <button key={k} data-testid={`ig-cat-${k}`} onClick={() => selectStat(k, DEFAULT_STAT[k])} aria-pressed={category === k}
            className={`whitespace-nowrap px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border transition-colors ${category === k ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-300 text-[#0A192F] hover:bg-slate-50"}`}>
            {lbl}
          </button>
        ))}
      </div>

      {/* STEP 2 — statistic + team context */}
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="flex flex-col gap-1">
          <span className="label-overline text-[10px]">Statistic</span>
          <select data-testid="ig-stat-select" value={stat} onChange={(e) => selectStat(category, e.target.value)}
            className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white min-w-[220px] font-semibold text-[#0A192F]">
            {statList.map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="label-overline text-[10px]">Team Context</span>
          <select data-testid="ig-context-select" value={context} onChange={(e) => set({ ctx: e.target.value === "all" ? "" : e.target.value })}
            className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white min-w-[190px]">
            {CONTEXTS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </label>
      </div>

      {/* Quick stat chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="ig-quick-chips">
        {(CHIPS[category] || []).filter((s) => statList.includes(s)).map((s) => (
          <button key={s} data-testid={`ig-chip-${s}`} onClick={() => selectStat(category, s)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full border transition-colors ${stat === s ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white border-slate-300 text-slate-600 hover:border-[#E63946] hover:text-[#E63946]"}`}>
            {label(s)}
          </button>
        ))}
      </div>

      {/* Secondary filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Sel testid="ig-filter-grad" label="Grad Year" value={f.graduation_year} onChange={(v) => set({ grad: v })}>
          <option value="">All</option>{(cfg?.graduation_years || []).map((y) => <option key={y} value={y}>{y}</option>)}
        </Sel>
        <Sel testid="ig-filter-prov" label="Province" value={f.province} onChange={(v) => set({ prov: v })}>
          <option value="">All</option>{PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
        </Sel>
        <Sel testid="ig-filter-pos" label="Position" value={f.position} onChange={(v) => set({ pos: v })}>
          <option value="">All</option>{POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </Sel>
        <Sel testid="ig-filter-tourn" label="Tournament" value={f.tournament_id} onChange={(v) => set({ tourn: v })}>
          <option value="">All</option>{(cfg?.tournaments || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Sel>
      </div>

      {/* Context header bar */}
      {data && (
        <div data-testid="ig-context-bar" className="bg-[#0A192F] text-white rounded-sm px-4 py-3 mb-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
          <span className="font-display text-base uppercase tracking-tight">{label(stat)}</span>
          <span className="text-white/60">{CATEGORIES.find(([k]) => k === category)?.[1]} · {CONTEXTS.find(([k]) => k === context)?.[1]}</span>
          <span className="text-white/80">Qualified: <b>{data.qualified_count}</b></span>
          <span className="text-white/80">Approved participants: <b>{data.approved_participants}</b></span>
          {data.required_sample != null && kind !== "count" && <span className="text-white/80">Min sample: <b>{data.required_sample}</b></span>}
          <span className="text-white/80">Comparison group: <b>{data.min_comparison_group}</b></span>
          {statusBadge && <span className={`ml-auto px-2 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${statusBadge[1]}`}>{statusBadge[0]} Ranks</span>}
        </div>
      )}

      {data?.provisional && (
        <div data-testid="ig-provisional" className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2 mb-3">
          <Info className="w-4 h-4 shrink-0" /> {data.qualified_count} qualified {data.qualified_count === 1 ? "player" : "players"} currently meet the {label(stat)} minimum. Rankings are provisional until at least {data.min_comparison_group} players qualify.
        </div>
      )}

      {/* Main table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        {loading ? (
          <div className="p-10 label-overline text-slate-500">Loading…</div>
        ) : !data || data.items.length === 0 ? (
          <div className="p-10 text-center" data-testid="ig-empty">
            <Trophy className="w-12 h-12 mx-auto text-slate-300" />
            <div className="font-display text-2xl uppercase mt-4">No ranked players yet</div>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
              {kind === "count" ? "No approved participants for this board yet." : `No players have reached the ${label(stat)} minimum sample yet. Try a counting-stat board that shows every approved participant.`}
            </p>
            {kind !== "count" && (
              <button onClick={() => selectStat(category, DEFAULT_STAT[category])} className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#E63946] hover:underline">
                View {label(DEFAULT_STAT[category])} leaderboard <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm min-w-max">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-3 label-overline w-12">Rank</th>
                <th className="text-left p-3 label-overline">Player</th>
                <th className="text-left p-3 label-overline">Class</th>
                <th className="text-left p-3 label-overline">Pos</th>
                <th className="text-left p-3 label-overline">Prov</th>
                <th className="text-right p-3 label-overline text-[#E63946]">{label(stat)}</th>
                <th className="text-right p-3 label-overline">G</th>
                <th className="text-right p-3 label-overline">Conf</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r, i) => (
                <tr key={r.player_id} data-testid={`ig-row-${r.player_id}`} className={`border-b border-slate-100 last:border-0 ${i < 3 && data.ranks_status === "official" ? "bg-amber-50/40" : ""}`}>
                  <td className="p-3 font-data text-lg">
                    {r.rank === 1 ? <span className="text-[#E63946]">#1</span> : r.rank === 2 ? <span className="text-[#0A192F]">#2</span> : r.rank === 3 ? <span className="text-amber-700">#3</span> : <span className="text-slate-500">#{r.rank}</span>}
                  </td>
                  <td className="p-3">
                    {r.player?.slug ? <Link to={`/prospects/${r.player.slug}`} className="font-semibold hover:text-[#E63946]">{r.player.first_name} {r.player.last_name}</Link>
                      : <span className="font-semibold">{r.player?.first_name} {r.player?.last_name}</span>}
                    <ShieldCheck className="inline w-3.5 h-3.5 text-[#E63946] ml-1.5" title="Verified CBR Game Data" />
                  </td>
                  <td className="p-3 font-data">{r.graduation_year || EMDASH}</td>
                  <td className="p-3 font-data">{r.position || EMDASH}</td>
                  <td className="p-3">{r.province || EMDASH}</td>
                  <td className="p-3 text-right font-data tabular-nums text-[#E63946] font-bold text-base">{fmtVal(stat, r.value)}</td>
                  <td className="p-3 text-right font-data tabular-nums text-slate-600">{r.games}</td>
                  <td className="p-3 text-right"><span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${CONF[r.confidence] || CONF.low}`}>{r.confidence}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Not Yet Qualified */}
      {data?.not_yet_qualified?.length > 0 && (
        <div className="mt-5" data-testid="ig-not-qualified">
          <h4 className="label-overline text-slate-500 mb-2">Not Yet Qualified</h4>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm min-w-max">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left p-3 label-overline">Player</th>
                  <th className="text-right p-3 label-overline">{label(stat)}</th>
                  <th className="text-right p-3 label-overline">Current Sample</th>
                  <th className="text-right p-3 label-overline">Required</th>
                  <th className="text-left p-3 label-overline">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.not_yet_qualified.map((r) => (
                  <tr key={r.player_id} data-testid={`ig-nq-${r.player_id}`} className="border-b border-slate-100 last:border-0 text-slate-500">
                    <td className="p-3">
                      {r.player?.slug ? <Link to={`/prospects/${r.player.slug}`} className="font-semibold text-slate-700 hover:text-[#E63946]">{r.player.first_name} {r.player.last_name}</Link>
                        : <span className="font-semibold text-slate-700">{r.player?.first_name} {r.player?.last_name}</span>}
                    </td>
                    <td className="p-3 text-right font-data">{fmtVal(stat, r.value)}</td>
                    <td className="p-3 text-right font-data">{r.current_sample} {r.sample_label}</td>
                    <td className="p-3 text-right font-data">{r.required_sample}</td>
                    <td className="p-3 text-xs">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#E63946]" /> Based on Approved CBR Game Data · counting stats show every approved participant · rate stats require a qualifying sample</p>
    </div>
  );
}

function Sel({ label: lbl, value, onChange, children, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="label-overline text-[10px]">{lbl}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white min-w-[130px]">{children}</select>
    </label>
  );
}
